import { extractImageUrls } from './toolImages';

const DELTA_EVENT_TYPES = new Set(['assistant_delta', 'thinking_delta']);

const eventText = (event) => {
  if (typeof event?.content === 'object' && event.content?.text !== undefined) {
    return event.content.text;
  }
  return typeof event?.content === 'string' ? event.content : '';
};

const subAgentKey = (event) => {
  const info = event?.sub_agent_info;
  if (!info) return '';
  return info.dispatch_id || info.agent_id || info.conversation_id || info.agent_name || 'sub-agent';
};

const eventKey = (event) => {
  if (!event?.uuid) return null;
  if (DELTA_EVENT_TYPES.has(event.event_type)) {
    return `${event.event_type}:${subAgentKey(event)}:${event.uuid}`;
  }
  return event.uuid;
};

const eventTimestamp = (value) => {
  const timestamp = new Date(value);
  return Number.isNaN(timestamp.getTime()) ? new Date() : timestamp;
};

const messageType = (event) => {
  if (event.event_type === 'thinking_delta') return 'thinking';
  if (event.event_type === 'assistant_delta') return 'text';
  return event.content?.message_type;
};

const projectTranscriptEvent = (event) => {
  if (event?.event_type === 'chat_message') return event;
  if (DELTA_EVENT_TYPES.has(event?.event_type) && event.sub_agent_info) return event;
  return null;
};

/**
 * Fold one transcript-bearing AgentEvent into the existing Studio messages.
 *
 * Parent-agent deltas are intentionally inert because Studio renders the
 * generator's synthesized chat_message. Delegated agents have no synthesized
 * prose path, so their deltas are accumulated by event type, agent, and UUID.
 */
export const applyAgentEventToMessages = (messages, incomingEvent, currentAgentType) => {
  const event = projectTranscriptEvent(incomingEvent);
  if (!event) return messages;

  const key = eventKey(event);
  const newContent = eventText(event);
  const newImages = extractImageUrls(event.content?.images);
  const index = key
    ? messages.findIndex((message) => message.eventKey === key || (!message.eventKey && message.uuid === key))
    : -1;

  const subAgentInfo = event.sub_agent_info || null;
  const agentName = subAgentInfo?.agent_name;
  const isStreaming = typeof event.is_streaming === 'boolean'
    ? event.is_streaming
    : !event.content?.complete;

  if (index >= 0) {
    const updatedMessages = [...messages];
    const existing = updatedMessages[index];
    updatedMessages[index] = {
      ...existing,
      content: `${existing.content || ''}${newContent || ''}`,
      isStreaming,
      message_type: messageType(event) || existing.message_type || 'text',
      tool_description: event.content?.tool_description || existing.tool_description,
      tool_call_id: event.content?.tool_call_id || existing.tool_call_id,
      images: newImages.length > 0 ? newImages : existing.images,
      lastUpdated: new Date(),
      agentType: agentName || existing.agentType ||
        ((event.sender === 'agent' || !event.sender) ? currentAgentType : undefined),
      isSubAgent: Boolean(subAgentInfo) || existing.isSubAgent || false,
      subAgentInfo: subAgentInfo || existing.subAgentInfo,
    };
    return updatedMessages;
  }

  if ((!newContent || newContent.trim() === '') && newImages.length === 0) return messages;

  return [
    ...messages,
    {
      sender: agentName || event.sender || 'agent',
      content: newContent,
      timestamp: eventTimestamp(event.timestamp),
      uuid: event.uuid,
      eventKey: key,
      lastUpdated: new Date(),
      isStreaming,
      message_type: messageType(event) || 'text',
      tool_description: event.content?.tool_description,
      tool_call_id: event.content?.tool_call_id,
      images: newImages,
      agentType: agentName ||
        ((event.sender === 'agent' || !event.sender) ? currentAgentType : undefined),
      isSubAgent: Boolean(subAgentInfo),
      subAgentInfo,
    },
  ];
};
